import type {
  IChatMessageUIHints,
  IChatProcessingStep,
} from '@/apiClient/types/chat'

/**
 * Merges streamed/chunk `ui_hints` with optional drained `processing_meta` buffer rows.
 * Chunk `processing_steps` wins when present; otherwise buffer is appended to `prev`.
 */
export function mergeAssistantUiHints(
  prev: IChatMessageUIHints | undefined,
  chunkHints: IChatMessageUIHints | undefined,
  drainedBuffer: IChatProcessingStep[],
): IChatMessageUIHints | undefined {
  const mergedSteps =
    chunkHints?.processing_steps ??
    (drainedBuffer.length > 0
      ? [...(prev?.processing_steps ?? []), ...drainedBuffer]
      : prev?.processing_steps)

  if (!chunkHints && drainedBuffer.length === 0 && !prev) {
    return undefined
  }

  return {
    ...prev,
    ...chunkHints,
    version: chunkHints?.version ?? prev?.version ?? 1,
    processing_steps: mergedSteps,
  }
}

/**
 * Hints with one pipeline step dropped, e.g. `pptx_generation` once the deck is delivered.
 * Everything else stays — the other steps (a reply can run several jobs), the tool calls behind
 * the reply, and the question it asks with its answers — so the steps list and the answer card
 * survive the job finishing. Nothing left but `version` means no hints at all.
 */
export function withoutProcessingStep(
  hints: IChatMessageUIHints | undefined,
  stepName: string,
): IChatMessageUIHints | undefined {
  if (!hints) return undefined
  const { processing_steps: processingSteps, ...rest } = hints
  const remainingSteps = (processingSteps ?? []).filter(
    (step) => step.step !== stepName,
  )
  if (remainingSteps.length > 0) {
    return { ...rest, processing_steps: remainingSteps }
  }
  const hasOtherHints = Object.entries(rest).some(
    ([key, value]) => key !== 'version' && value != null,
  )
  return hasOtherHints ? rest : undefined
}

/** Returns a new map without `chatId` and the removed steps (immutable drain). */
export function drainBufferedProcessingSteps(
  map: Record<string, IChatProcessingStep[]>,
  chatId: string,
): {
  map: Record<string, IChatProcessingStep[]>
  drained: IChatProcessingStep[]
} {
  const steps = map[chatId]
  if (!steps?.length) return { map, drained: [] }
  const next = { ...map }
  delete next[chatId]
  return { map: next, drained: steps }
}

/** Immutable append of one `processing_meta` row to the per-chat buffer. */
export function appendProcessingMetaToBuffer(
  map: Record<string, IChatProcessingStep[]>,
  row: IChatProcessingStep,
): Record<string, IChatProcessingStep[]> {
  const bufferChatId = row.chat_id
  return {
    ...map,
    [bufferChatId]: [...(map[bufferChatId] ?? []), row],
  }
}

export function normalizeProcessingMetaRow(
  step: IChatProcessingStep,
  nowSec: number,
): IChatProcessingStep {
  return {
    ...step,
    timestamp: step.timestamp ?? nowSec,
    data: step.data ?? {},
  }
}
